import { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Plus, Edit, Trash2, Loader2, Image, Upload, 
  Layers, Search, AlertCircle, FolderPlus
} from 'lucide-react';
import { toast } from 'sonner';

export default function AmenitiesMasterPage({ user }) {
  const [amenities, setAmenities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [amenityDialogOpen, setAmenityDialogOpen] = useState(false);
  
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: ''
  });
  
  const [amenityForm, setAmenityForm] = useState({
    group_id: '',
    name: '',
    description: ''
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, amenitiesRes] = await Promise.all([
        api.get('/amenity-management/groups'),
        api.get('/amenity-management').catch(() => ({ data: [] }))
      ]);
      
      setGroups(groupsRes.data);
      setAmenities(amenitiesRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // ==================== GROUP HANDLERS ====================
  
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!groupForm.name) {
      toast.error('Please enter a group name');
      return;
    }
    
    setSaving(true);
    try {
      if (editingGroup) {
        await api.put(`/amenity-management/groups/${editingGroup.id}`, groupForm);
        toast.success('Amenity group updated');
      } else {
        await api.post('/amenity-management/groups', groupForm);
        toast.success('Amenity group created');
      }
      closeGroupDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupForm({ name: '', description: '' });
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || ''
    });
    setGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (group) => {
    if (!confirm(`Are you sure you want to delete "${group.name}"?`)) return;
    
    try {
      await api.delete(`/amenity-management/groups/${group.id}`);
      toast.success('Amenity group deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  // ==================== AMENITY HANDLERS ====================
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        toast.error('Image size should be less than 500KB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAmenitySubmit = async (e) => {
    e.preventDefault();
    if (!amenityForm.group_id || !amenityForm.name) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const submitData = new FormData();
      submitData.append('group_id', amenityForm.group_id);
      submitData.append('name', amenityForm.name);
      if (amenityForm.description) submitData.append('description', amenityForm.description);
      if (imageFile) submitData.append('icon', imageFile);
      
      if (editingAmenity) {
        await api.put(`/amenity-management/${editingAmenity.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Amenity updated');
      } else {
        await api.post('/amenity-management', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Amenity created');
      }
      
      closeAmenityDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save amenity');
    } finally {
      setSaving(false);
    }
  };

  const closeAmenityDialog = () => {
    setAmenityDialogOpen(false);
    setEditingAmenity(null);
    setAmenityForm({ group_id: '', name: '', description: '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleEditAmenity = (amenity) => {
    setEditingAmenity(amenity);
    setAmenityForm({
      group_id: amenity.group_id,
      name: amenity.name,
      description: amenity.description || ''
    });
    setImagePreview(amenity.icon_url || null);
    setAmenityDialogOpen(true);
  };

  const handleDeleteAmenity = async (amenity) => {
    if (!confirm(`Are you sure you want to delete "${amenity.name}"?`)) return;
    
    try {
      await api.delete(`/amenity-management/${amenity.id}`);
      toast.success('Amenity deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete amenity');
    }
  };

  // ==================== FILTERING ====================
  
  const filteredAmenities = amenities.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = selectedGroup === 'all' || a.group_id === selectedGroup;
    return matchesSearch && matchesGroup;
  });

  const groupedAmenities = groups.map(g => ({
    ...g,
    amenities: filteredAmenities.filter(a => a.group_id === g.id)
  })).filter(g => selectedGroup === 'all' || g.id === selectedGroup);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Layers className="h-6 w-6 text-indigo-600" />
            Amenities Management
          </h1>
          <p className="text-gray-500 mt-1">
            {isSuperAdmin 
              ? "Create and manage global amenity groups and amenities"
              : "View available amenities"}
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setGroupDialogOpen(true)}
              data-testid="add-group-btn"
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Amenity Group
            </Button>
            <Button 
              onClick={() => setAmenityDialogOpen(true)}
              data-testid="add-amenity-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Amenity
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="amenities">
        <TabsList className="mb-6">
          <TabsTrigger value="amenities" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Amenities
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2">
            <FolderPlus className="h-4 w-4" />
            Amenity Groups
          </TabsTrigger>
        </TabsList>

        {/* Amenities Tab */}
        <TabsContent value="amenities">
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search amenities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSuperAdmin && (
            <Card className="mb-4 border-blue-200 bg-blue-50">
              <CardContent className="py-3 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Create global amenities here. Company Admins will allocate these amenities to their hotels via the Hotels configuration.
                </p>
              </CardContent>
            </Card>
          )}

          {amenities.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Amenities Created</h3>
                <p className="text-gray-500 mb-4">
                  Create your first global amenity to get started
                </p>
                {isSuperAdmin && (
                  <Button onClick={() => setAmenityDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Amenity
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {groupedAmenities.map(group => (
                <Card key={group.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge variant="outline">{group.level}</Badge>
                      {group.name}
                    </CardTitle>
                    <CardDescription>{group.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {group.amenities.length === 0 ? (
                      <p className="text-gray-500 text-sm py-4 text-center">
                        No amenities in this group
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.amenities.map(amenity => (
                          <div
                            key={amenity.id}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            data-testid={`amenity-${amenity.id}`}
                          >
                            <div className="w-14 h-14 bg-white rounded-lg border flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {amenity.icon_url ? (
                                <img 
                                  src={amenity.icon_url} 
                                  alt={amenity.name}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              ) : (
                                <Image className="h-6 w-6 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">{amenity.name}</p>
                              <p className="text-xs text-gray-500 truncate">{amenity.description || 'No description'}</p>
                              {amenity.hotel_assignment_count > 0 && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Assigned to {amenity.hotel_assignment_count} hotel(s)
                                </p>
                              )}
                            </div>
                            {isSuperAdmin && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditAmenity(amenity)}
                                  title="Edit amenity"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAmenity(amenity)}
                                  className={amenity.can_delete ? "text-red-600 hover:text-red-700 hover:bg-red-50" : "text-gray-300 cursor-not-allowed"}
                                  disabled={!amenity.can_delete}
                                  title={amenity.can_delete ? "Delete amenity" : `Cannot delete - assigned to ${amenity.hotel_assignment_count} hotel(s)`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <Card>
            <CardHeader>
              <CardTitle>Amenity Groups ({groups.length})</CardTitle>
              <CardDescription>
                Categories for organizing amenities
              </CardDescription>
            </CardHeader>
            <CardContent>
              {groups.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No amenity groups created</p>
                  {isSuperAdmin && (
                    <Button onClick={() => setGroupDialogOpen(true)}>
                      <FolderPlus className="h-4 w-4 mr-2" />
                      Add Amenity Group
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amenities</TableHead>
                      <TableHead>Created</TableHead>
                      {isSuperAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groups.map((group) => (
                      <TableRow key={group.id}>
                        <TableCell className="font-medium">{group.name}</TableCell>
                        <TableCell className="text-gray-500 max-w-xs truncate">
                          {group.description || '-'}
                        </TableCell>
                        <TableCell>
                          {amenities.filter(a => a.group_id === group.id).length}
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {new Date(group.created_at).toLocaleDateString()}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditGroup(group)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteGroup(group)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={(open) => !open && closeGroupDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? 'Edit Amenity Group' : 'Create Amenity Group'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGroupSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="group-name">Group Name *</Label>
              <Input
                id="group-name"
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                placeholder="e.g., Room Amenities"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="group-description">Description</Label>
              <Input
                id="group-description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                placeholder="Brief description of this group"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={closeGroupDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingGroup ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Amenity Dialog */}
      <Dialog open={amenityDialogOpen} onOpenChange={(open) => !open && closeAmenityDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAmenity ? 'Edit Amenity' : 'Create Amenity'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAmenitySubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="amenity-group">Group *</Label>
              <Select
                value={amenityForm.group_id}
                onValueChange={(value) => setAmenityForm({ ...amenityForm, group_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="amenity-name">Amenity Name *</Label>
              <Input
                id="amenity-name"
                value={amenityForm.name}
                onChange={(e) => setAmenityForm({ ...amenityForm, name: e.target.value })}
                placeholder="e.g., Free WiFi"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="amenity-description">Description</Label>
              <Input
                id="amenity-description"
                value={amenityForm.description}
                onChange={(e) => setAmenityForm({ ...amenityForm, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            
            <div>
              <Label>Image</Label>
              <div className="mt-2 flex items-center gap-4">
                <div 
                  className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-indigo-400 transition-colors overflow-hidden bg-gray-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                      <span className="text-xs text-gray-500">Upload</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="text-sm text-gray-500">
                  <p>Click to upload image</p>
                  <p className="text-xs">PNG, JPG, WEBP (max 500KB)</p>
                  {imagePreview && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="mt-1 text-red-600"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                    >
                      Remove Image
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={closeAmenityDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingAmenity ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
